import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Mail, Phone, MapPin, Shield, ExternalLink } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';

export default function ImpressumPage() {
  return (
    <Layout>
      <SEOHead
        title="Impressum"
        description="Impressum von DeinServerHost – Angaben gemäß § 5 TMG."
        canonical="/imprint"
        noindex={true}
      />
      {/* Hero */}
      <section className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
          <p className="text-xs font-mono text-primary uppercase tracking-widest mb-3">Rechtliches</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-foreground">Impressum</h1>
          <p className="mt-4 text-muted-foreground">Gesetzlich vorgeschriebene Anbieterkennung gemäß § 5 TMG</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 space-y-12">

        {/* Anbieter */}
        <section data-testid="impressum-anbieter">
          <h2 className="text-2xl font-bold mb-6 text-foreground border-b border-border pb-3">Gesetzliche Anbieterkennung</h2>
          <div className="bg-card rounded-xl border border-border p-6 space-y-4">
            <p className="text-xl font-semibold text-foreground">Christian Ralph Hennig</p>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-3 text-muted-foreground">
                <MapPin className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                <div>
                  <p>Kirchplatz 17</p>
                  <p>66571 Eppelborn</p>
                  <p>Deutschland</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Phone className="w-4 h-4 text-primary shrink-0" />
                  <div>
                    <p>Telefon: +49 6881 59591 00</p>
                    <p>Notfall: +49 6881 59591 99</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Mail className="w-4 h-4 text-primary shrink-0" />
                  <a href="mailto:info@deinserverhost.de" className="hover:text-foreground transition-colors">
                    info@deinserverhost.de
                  </a>
                </div>
              </div>
            </div>
            <div className="pt-2 border-t border-border text-sm text-muted-foreground">
              <p><span className="text-foreground font-medium">USt-IdNr.:</span> DE290815944</p>
            </div>
          </div>
        </section>

        {/* Verantwortlicher */}
        <section>
          <h2 className="text-2xl font-bold mb-4 text-foreground border-b border-border pb-3">Inhaltlich Verantwortlicher</h2>
          <p className="text-muted-foreground">
            Inhaltlich Verantwortlicher gemäß § 55 RStV: <span className="text-foreground font-medium">Christian Ralph Hennig</span>, Anschrift wie vorstehend.
          </p>
          <p className="mt-3 text-muted-foreground">
            Wir sind nicht bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor Verbraucherschlichtungsstellen teilzunehmen.
          </p>
        </section>

        {/* Kontaktstellen */}
        <section>
          <h2 className="text-2xl font-bold mb-4 text-foreground border-b border-border pb-3">Kontaktstellen</h2>
          <div className="space-y-6">
            <div className="bg-card rounded-xl border border-border p-5">
              <h3 className="font-semibold text-foreground mb-2">
                Kontaktstelle gemäß Verordnung (EU) 2021/784
              </h3>
              <p className="text-sm text-muted-foreground mb-2">
                E-Mail:{' '}
                <a href="mailto:tco-contact@deinserverhost.de" className="text-primary hover:underline">
                  tco-contact@deinserverhost.de
                </a>
              </p>
              <p className="text-sm text-muted-foreground">
                Kontaktaufnahme möglich in: Deutsch und Englisch
              </p>
              <p className="text-xs text-muted-foreground/70 mt-2 italic">
                Diese E-Mail-Adresse ist ausschließlich für die Kommunikation gemäß Verordnung (EU) 2021/784 bestimmt. Andere Anfragen werden nicht beantwortet.
              </p>
            </div>

            <div className="bg-card rounded-xl border border-border p-5">
              <h3 className="font-semibold text-foreground mb-2">
                Kontaktstelle für Nutzer und Behörden gemäß Verordnung (EU) 2022/2065 (Digital Services Act)
              </h3>
              <p className="text-sm text-muted-foreground mb-2">
                E-Mail:{' '}
                <a href="mailto:abusecontact@deinserverhost.de" className="text-primary hover:underline">
                  abusecontact@deinserverhost.de
                </a>
              </p>
              <p className="text-sm text-muted-foreground">
                Kontaktaufnahme möglich in: Deutsch, Englisch
              </p>
            </div>
          </div>
        </section>

        {/* FairCommerce */}
        <section>
          <div className="flex items-start gap-4 bg-primary/5 border border-primary/20 rounded-xl p-5">
            <Shield className="w-6 h-6 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-foreground">Mitglied der Initiative „FairCommerce"</p>
              <p className="text-sm text-muted-foreground mt-1">
                Nähere Informationen finden Sie unter{' '}
                <a
                  href="https://www.haendlerbund.de/faircommerce"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  www.haendlerbund.de/faircommerce <ExternalLink className="w-3 h-3" />
                </a>
              </p>
            </div>
          </div>
        </section>

        {/* Bildquellen */}
        <section>
          <h2 className="text-2xl font-bold mb-4 text-foreground border-b border-border pb-3">Quellenangaben</h2>
          <ul className="space-y-1 text-sm text-muted-foreground list-disc list-inside">
            <li><a href="http://playrust.com/" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">playrust.com</a></li>
            <li><a href="http://www.reignofkings.net/" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">reignofkings.net</a></li>
            <li>Server datacenter panels © Oleksandr Delyk #86289014 – Fotolia</li>
            <li>Server datacenter panels © Evgen3d #119647168 – Fotolia</li>
            <li>Server datacenter panels © Audy_Indy #125505563 – Fotolia</li>
          </ul>
        </section>

        {/* Footer Nav */}
        <div className="pt-6 border-t border-border flex flex-wrap gap-4 text-sm text-muted-foreground">
          <Link to="/privacy" className="hover:text-foreground transition-colors">Datenschutzerklärung</Link>
          <Link to="/terms" className="hover:text-foreground transition-colors">AGB</Link>
          <Link to="/cookie-settings" className="hover:text-foreground transition-colors">Cookie-Einstellungen</Link>
        </div>
      </div>
    </Layout>
  );
}
